import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import { Helmet } from "react-helmet";

import { useModuleLoader } from "../module-loader/module-loader-context";
import { ModuleRenderer } from "../module-renderer";
import { ViewIdentityProvider } from "../view-identity";

import { LoadingModuleHost, loadingModuleKey } from "./loading-module-host";
import { useLoadingSlot } from "./loading-slot-context";
import { RenderFallbackBoundary } from "./render-fallback-boundary";
import { useHeldLoadingDisplay } from "./use-held-loading-display";

/**
 * First-load loading state, rendered as the shell route's
 * `hydrateFallbackElement` until the initial `/load` settles (including across
 * an initial in-shell redirect).
 *
 * Renders the flow's loading module inside the streamed layout once the
 * server sends the layout's data, standalone when the server says no early
 * layout is coming (failed guard, resolver view), and nothing before that or
 * when the flow has no loading module. The layout renders with inert actions:
 * its view hasn't committed, so nothing may be submitted yet.
 *
 * While the layout's bundle downloads, and if it fails to load or the layout
 * throws while rendering, the standalone loading module shows instead, so
 * loading never blanks out or reaches the route's error boundary while the
 * real load is still in flight.
 */
export function ShellLoadingFallback(): ReactNode {
  const slot = useLoadingSlot();
  const loader = useModuleLoader();
  const display = useHeldLoadingDisplay(slot, true);

  if (display === undefined || display.kind === "waiting") {
    return null;
  }

  const { loadingModule } = display;
  const standalone = (
    <LoadingModuleHost
      key={loadingModuleKey(loadingModule)}
      loadingModule={loadingModule}
      placement="page"
    />
  );

  return (
    <>
      {display.flowMeta && (
        <Helmet>
          <title>{display.flowMeta.title}</title>
        </Helmet>
      )}
      {display.kind === "page" ? (
        standalone
      ) : (
        <RenderFallbackBoundary
          key={`${display.layout.moduleId}@${display.layout.version}`}
          fallback={standalone}
        >
          <ViewIdentityProvider
            value={{
              viewId: display.loadedView.viewId,
              viewVersion: display.loadedView.viewVersion,
            }}
          >
            <ModuleRenderer
              moduleId={display.layout.moduleId}
              version={display.layout.version}
              loader={loader}
              props={display.layout.props}
              data={display.layout.data}
              loadingFallback={standalone}
              actionsMode="inert"
            >
              <LoadingModuleHost
                key={loadingModuleKey(loadingModule)}
                loadingModule={loadingModule}
                placement="layout"
              />
            </ModuleRenderer>
          </ViewIdentityProvider>
        </RenderFallbackBoundary>
      )}
    </>
  );
}
